import { Link, NavLink, useNavigate } from "react-router-dom";
import { BookOpen, Building2, CalendarCheck, Home, LogIn, Mail, Menu, MessageCircle, Phone, X } from "lucide-react";
import { useState } from "react";
import { resolveWebsiteImage, whatsappUrl } from "../../lib/websiteContent";
import { useAuth } from "../../context/AuthContext";
import LoginModal from "../LoginModal";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/e-catalogue", label: "E-Catalogue" },
  { to: "/industries", label: "Industries" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About Us" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact Us" },
];

export default function EnterpriseShell({ content, children }) {
  const [open, setOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const { user } = useAuth();
  const settings = content?.settings || {};
  const primaryCta = content?.home?.primaryCta || "Book Live Demo";
  const navigate = useNavigate();
  const logo = resolveWebsiteImage(settings.logo || "/logo.png");

  const goDemo = () => {
    setOpen(false);
    setLoginOpen(false);
    navigate("/contact#demo");
    setTimeout(() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const openLogin = () => {
    setOpen(false);
    if (user && user !== false) {
      navigate(user.role === "admin" ? "/admin" : user.role === "dealer" ? "/dealer" : "/client");
      return;
    }
    setLoginOpen(true);
  };

  const accountLabel = user && user !== false ? "Dashboard" : "Login";

  return (
    <div className="min-h-screen bg-white text-[#07142f]">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur">
        <div className="mx-auto flex h-20 max-w-[1500px] items-center justify-between gap-3 px-3 lg:px-6 xl:px-8">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex min-w-0 flex-1 items-center gap-3 lg:flex-none xl:gap-4">
            <img src={logo} alt={`${settings.brandName || "RP Infotech"} logo`} className="h-14 w-16 shrink-0 rounded-sm object-contain sm:h-16 sm:w-20 lg:h-16 lg:w-20" />
            <div className="min-w-0">
              <div className="truncate text-lg font-black uppercase tracking-tight text-[#07142f] xl:text-xl">{settings.brandName || "RP Infotech"}</div>
              <div className="truncate text-[11px] font-semibold text-slate-500">{settings.tagline || "LED Displays & Digital Signage Solutions"}</div>
            </div>
          </Link>
          <nav className="hidden min-w-0 items-center justify-center gap-0.5 lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-bold transition xl:px-3 xl:text-sm ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-800 hover:bg-slate-100"}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-3">
            <a href={`tel:${String(settings.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-1.5 whitespace-nowrap text-xs font-black text-[#07142f] xl:text-sm">
              <Phone className="h-4 w-4 text-blue-600" /> {settings.phone || "+91 93230 37254"}
            </a>
            <button
              onClick={openLogin}
              className="flex items-center gap-2 whitespace-nowrap rounded-xl border border-slate-200 px-3 py-3 text-xs font-black text-slate-900 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 xl:px-4 xl:text-sm"
            >
              <LogIn className="h-4 w-4 text-blue-600" /> Login
            </button>
            <button onClick={goDemo} className="whitespace-nowrap rounded-xl bg-orange-500 px-4 py-3 text-xs font-black text-white shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:bg-orange-600 xl:px-5 xl:text-sm">
              {primaryCta}
            </button>
          </div>
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <button
              onClick={openLogin}
              className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-slate-950 px-3 text-xs font-black text-white shadow-sm"
              aria-label={accountLabel}
            >
              <LogIn className="h-4 w-4" />
              <span>{accountLabel}</span>
            </button>
            <button className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 bg-white" onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] bg-white text-slate-950 lg:hidden">
          <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
            <div className="text-base font-black">{settings.brandName || "RP Infotech"}</div>
            <button className="grid h-10 w-10 place-items-center rounded-full hover:bg-slate-100" onClick={() => setOpen(false)} aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="divide-y divide-slate-200">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="flex h-12 items-center justify-between px-4 text-[15px] font-semibold">
                {item.label}
              </Link>
            ))}
          </div>
          <div className="border-t border-slate-200 p-4">
            <button onClick={openLogin} className="flex h-11 w-full items-center justify-center gap-2 rounded-sm border border-slate-300 bg-white text-sm font-bold">
              <LogIn className="h-4 w-4" /> {accountLabel}
            </button>
            <button onClick={goDemo} className="mt-3 h-11 w-full rounded-sm bg-orange-500 text-sm font-black text-white">{primaryCta}</button>
          </div>
        </div>
      )}

      {children}

      <footer className="bg-[#061327] pb-20 pt-12 text-white lg:pb-0">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-4 lg:grid-cols-[1.3fr_2fr] lg:px-8">
          <div>
            <div className="text-2xl font-black">{settings.brandName || "RP Infotech"}</div>
            <p className="mt-3 max-w-md text-sm leading-6 text-slate-300">{settings.footer}</p>
            <div className="mt-5 grid gap-3 text-sm text-slate-200">
              <a className="flex items-center gap-2" href={`tel:${settings.phone}`}><Phone className="h-4 w-4 text-orange-400" />{settings.phone}</a>
              <a className="flex items-center gap-2" href={`mailto:${settings.email}`}><Mail className="h-4 w-4 text-orange-400" />{settings.email}</a>
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-orange-400" />{settings.address}</div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
              <button onClick={openLogin} className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-black text-white transition hover:bg-white/15">
                {accountLabel}
              </button>
              <button onClick={goDemo} className="rounded-xl bg-orange-500 px-4 py-3 text-sm font-black text-white transition hover:bg-orange-600">
                {primaryCta}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5 sm:gap-8">
            <FooterGroup title="Company" links={[["/about", "About Us"], ["/contact", "Contact Us"], ["/request-demo", "Request Demo"], ["/dealer-program", "Become a Dealer"]]} />
            <FooterGroup title="Products" links={content.products?.slice(0, 10).map((p) => [`/products/${p.slug}`, p.title])} />
            <FooterGroup title="Industries" links={content.industries?.slice(0, 6).map((p) => [`/industries/${p.slug}`, p.title])} />
            <FooterGroup title="Support" links={[["/contact", "Support"], ["/contact", "Installation"], ["/contact", "AMC"], ["/faq", "FAQ"]]} />
            <FooterGroup title="Legal" links={[["/privacy-policy", "Privacy Policy"], ["/terms-and-conditions", "Terms & Conditions"], ["/refund-policy", "Refund Policy"], ["/cookie-policy", "Cookie Policy"]]} />
          </div>
        </div>
        <div className="mx-auto mt-10 max-w-[1400px] border-t border-white/10 px-4 py-5 text-xs text-slate-400 lg:px-8">
          Copyright {new Date().getFullYear()} RP Infotech. LED TV, LED Active Display, LED Interactive Panel, LED Floor Stand & Wall Mounted Displays, Digital Signage Software.
        </div>
      </footer>

      <nav className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] text-[10px] font-bold text-slate-700 lg:hidden">
        <div className="grid grid-cols-5">
          <MobileAction to="/" label="Home" Icon={Home} />
          <MobileAction to="/e-catalogue" label="Catalogue" Icon={BookOpen} />
          <MobileAction as="a" href={whatsappUrl(settings)} label="WhatsApp" Icon={MessageCircle} className="text-green-600" />
          <MobileAction as="a" href={`tel:${String(settings.phone || "").replace(/\s/g, "")}`} label="Call" Icon={Phone} className="text-blue-700" />
          <MobileAction as="button" onClick={goDemo} label="Demo" Icon={CalendarCheck} className="text-orange-600" />
        </div>
      </nav>
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}

function FooterGroup({ title, links = [] }) {
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-black uppercase tracking-[0.14em] text-white">{title}</h3>
      <div className="mt-3 grid gap-2.5">
        {(links || []).map(([to, label]) => (
          <Link key={`${to}-${label}`} to={to} className="break-words text-sm leading-5 text-slate-300 hover:text-white">{label}</Link>
        ))}
      </div>
    </div>
  );
}

function MobileAction({ as = Link, to, href, onClick, label, Icon, className = "" }) {
  const baseClass = `flex min-h-[50px] min-w-0 flex-col items-center justify-center gap-0.5 px-1 text-center leading-tight transition active:bg-slate-100 ${className}`;
  const content = (
    <>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="w-full truncate">{label}</span>
    </>
  );

  if (as === "a") {
    return <a className={baseClass} href={href}>{content}</a>;
  }

  if (as === "button") {
    return <button type="button" className={baseClass} onClick={onClick}>{content}</button>;
  }

  return <Link className={baseClass} to={to}>{content}</Link>;
}
